import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Send, ArrowLeft, MoreVertical, Users as UsersIcon, 
  X, Edit3, Trash2, Check, XCircle 
} from 'lucide-react';
import { toast } from 'sonner';

const GroupChat = ({ user }) => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [group, setGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showMembersList, setShowMembersList] = useState(false);
  const [members, setMembers] = useState([]);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [userStatus, setUserStatus] = useState('online'); // 'online' or 'away'
  const [isStatusHidden, setIsStatusHidden] = useState(false); // Hide online status
  const [showGroupMenu, setShowGroupMenu] = useState(false);
  const [editingGroup, setEditingGroup] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [editedGroupName, setEditedGroupName] = useState('');
  const [editedGroupDescription, setEditedGroupDescription] = useState('');

  useEffect(() => {
    fetchGroupDetails();
    fetchMessages();
    fetchMembers();
    
    // Poll for new messages every 3 seconds
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [groupId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Update user status on activity
  useEffect(() => {
    let activityTimer;
    const resetTimer = () => {
      setUserStatus('online');
      clearTimeout(activityTimer);
      activityTimer = setTimeout(() => setUserStatus('away'), 5 * 60 * 1000); // 5 minutes
    };

    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keypress', resetTimer);
    resetTimer();

    return () => {
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keypress', resetTimer);
      clearTimeout(activityTimer);
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchGroupDetails = async () => {
    try {
      const response = await axios.get(`/groups/${groupId}`);
      setGroup(response.data);
    } catch (error) {
      console.error('Error fetching group details:', error);
      toast.error('Failed to load group details');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`/groups/${groupId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`/groups/${groupId}/members`);
      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await axios.post(`/groups/${groupId}/messages`, {
        content: newMessage.trim()
      });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleEditMessage = (message) => {
    setEditingMessageId(message.id);
    setEditedContent(message.content);
  };

  const handleSaveEdit = async (messageId) => {
    if (!editedContent.trim()) return;

    try {
      await axios.put(`/groups/${groupId}/messages/${messageId}`, {
        content: editedContent.trim()
      });
      toast.success('Message updated');
      setEditingMessageId(null);
      setEditedContent('');
      fetchMessages();
    } catch (error) {
      console.error('Error editing message:', error);
      toast.error('Failed to update message');
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditedContent('');
  };

  const handleDeleteMessage = async (messageId) => {
    setDeletingMessageId(messageId);
  };

  const confirmDeleteMessage = async () => {
    try {
      await axios.delete(`/groups/${groupId}/messages/${deletingMessageId}`);
      toast.success('Message deleted');
      setDeletingMessageId(null);
      fetchMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
      setDeletingMessageId(null);
    }
  };

  const getMemberStatus = (memberId) => {
    // Show hidden if user has hidden their status
    if (memberId === user.id && isStatusHidden) {
      return 'hidden';
    }
    // Show current user's actual status
    if (memberId === user.id) {
      return userStatus;
    }
    // For other users, show online (would come from backend in production)
    return 'online';
  };

  const handleEditGroup = () => {
    setEditedGroupName(group.name);
    setEditedGroupDescription(group.description);
    setEditingGroup(true);
    setShowGroupMenu(false);
  };

  const handleSaveGroupEdit = async () => {
    if (!editedGroupName.trim()) {
      toast.error('Group name cannot be empty');
      return;
    }

    try {
      await axios.put(`/groups/${groupId}`, {
        name: editedGroupName.trim(),
        description: editedGroupDescription.trim()
      });
      toast.success('Group updated successfully');
      setEditingGroup(false);
      fetchGroupDetails();
    } catch (error) {
      console.error('Error updating group:', error);
      toast.error('Failed to update group');
    }
  };

  const handleDeleteGroup = async () => {
    try {
      await axios.delete(`/groups/${groupId}`);
      toast.success('Group deleted successfully');
      navigate('/groups');
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    }
  };

  const toggleStatusVisibility = () => {
    setIsStatusHidden(!isStatusHidden);
    toast.success(isStatusHidden ? 'You are now visible' : 'You are now hidden');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-emerald-700 font-medium">Loading group chat...</p>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-emerald-700 font-medium">Group not found</p>
          <button onClick={() => navigate('/groups')} className="btn-primary mt-4">
            Back to Groups
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex">
      {/* Main Chat Area */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${showMembersList ? 'mr-80' : 'mr-0'}`}>
        {/* Header */}
        <div className="bg-white border-b border-emerald-200 p-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/groups')}
              className="p-2 hover:bg-emerald-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-emerald-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-emerald-800">{group.name}</h1>
              <p className="text-sm text-emerald-600">{members.length} members</p>
            </div>
          </div>
          
          {/* Three-Dot Menu */}
          <div className="relative">
            <button
              onClick={() => setShowGroupMenu(!showGroupMenu)}
              className="p-2 hover:bg-emerald-100 rounded-lg transition-colors"
              title="Menu"
            >
              <MoreVertical className="w-5 h-5 text-emerald-600" />
            </button>
            
            {showGroupMenu && (
              <>
                {/* Backdrop to close menu */}
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowGroupMenu(false)}
                />
                
                <div className="absolute right-0 mt-2 w-56 bg-white border border-emerald-200 rounded-lg shadow-xl z-20">
                  {/* View Members */}
                  <button
                    onClick={() => {
                      setShowMembersList(true);
                      setShowGroupMenu(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 flex items-center space-x-3 transition-colors"
                  >
                    <UsersIcon className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-800 font-medium">View Members</span>
                  </button>

                  {/* Hide/Show Status Toggle */}
                  <button
                    onClick={() => {
                      toggleStatusVisibility();
                      setShowGroupMenu(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 flex items-center justify-between transition-colors border-t border-emerald-100"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${isStatusHidden ? 'bg-gray-400' : 'bg-green-500'}`} />
                      <span className="text-emerald-800 font-medium">
                        {isStatusHidden ? 'Show My Status' : 'Hide My Status'}
                      </span>
                    </div>
                  </button>

                  {/* Divider - Only show for group creator */}
                  {group.creator_id === user.id && (
                    <div className="border-t border-emerald-200 my-1" />
                  )}

                  {/* Edit Group (Creator Only) */}
                  {group.creator_id === user.id && (
                    <button
                      onClick={() => {
                        handleEditGroup();
                        setShowGroupMenu(false);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-emerald-50 flex items-center space-x-3 transition-colors"
                    >
                      <Edit3 className="w-4 h-4 text-emerald-600" />
                      <span className="text-emerald-800 font-medium">Edit Group</span>
                    </button>
                  )}

                  {/* Delete Group (Creator Only) */}
                  {group.creator_id === user.id && (
                    <button
                      onClick={() => {
                        setDeletingGroup(true);
                        setShowGroupMenu(false);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center space-x-3 transition-colors border-t border-red-100"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                      <span className="text-red-600 font-medium">Delete Group</span>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-16">
              <div className="card max-w-md mx-auto">
                <UsersIcon className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-emerald-800 mb-2">No Messages Yet</h3>
                <p className="text-emerald-600">Be the first to start the conversation!</p>
              </div>
            </div>
          ) : (
            messages.map((message) => {
              const isOwn = message.user_id === user.id;
              const isEditing = editingMessageId === message.id;

              return (
                <div
                  key={message.id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                    {!isOwn && (
                      <span className="text-xs text-emerald-600 mb-1 px-2">
                        {message.user_name || 'Unknown User'}
                      </span>
                    )}
                    
                    <div className="group relative">
                      {isEditing ? (
                        <div className="bg-white border border-emerald-300 rounded-2xl p-3 shadow-sm">
                          <textarea
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            className="w-full border border-emerald-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            rows={3}
                            autoFocus
                          />
                          <div className="flex justify-end space-x-2 mt-2">
                            <button
                              onClick={handleCancelEdit}
                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                            >
                              <XCircle className="w-4 h-4 text-gray-600" />
                            </button>
                            <button
                              onClick={() => handleSaveEdit(message.id)}
                              className="p-1 hover:bg-emerald-100 rounded transition-colors"
                            >
                              <Check className="w-4 h-4 text-emerald-600" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div
                            className={`rounded-2xl px-4 py-2 shadow-sm ${
                              isOwn
                                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'
                                : 'bg-white text-emerald-900'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                            <p className={`text-xs mt-1 ${isOwn ? 'text-emerald-100' : 'text-emerald-500'}`}>
                              {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              {message.edited && ' (edited)'}
                            </p>
                          </div>

                          {/* Edit/Delete buttons for own messages */}
                          {isOwn && (
                            <div className="absolute -right-20 top-0 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                              <button
                                onClick={() => handleEditMessage(message)}
                                className="p-1.5 bg-white hover:bg-emerald-50 rounded-lg shadow-sm transition-colors"
                                title="Edit message"
                              >
                                <Edit3 className="w-4 h-4 text-emerald-600" />
                              </button>
                              <button
                                onClick={() => handleDeleteMessage(message.id)}
                                className="p-1.5 bg-white hover:bg-red-50 rounded-lg shadow-sm transition-colors"
                                title="Delete message"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className="bg-white border-t border-emerald-200 p-4">
          <form onSubmit={handleSendMessage} className="flex space-x-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 border border-emerald-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className="btn-primary rounded-full p-2 disabled:opacity-50"
            >
              {sending ? (
                <div className="spinner border-white w-5 h-5"></div>
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Members List Sidebar */}
      {showMembersList && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-30 z-40"
            onClick={() => setShowMembersList(false)}
          />
          
          <div className="fixed right-0 top-0 h-screen w-80 bg-white border-l border-emerald-200 shadow-2xl flex flex-col z-50">
            <div className="p-4 border-b border-emerald-200 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
              <h2 className="text-lg font-semibold text-emerald-800 flex items-center space-x-2">
                <UsersIcon className="w-5 h-5" />
                <span>Members ({members.length})</span>
              </h2>
              <button
                onClick={() => setShowMembersList(false)}
                className="p-1 hover:bg-emerald-200 rounded transition-colors"
              >
                <X className="w-5 h-5 text-emerald-600" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {members.map((member) => {
              const status = getMemberStatus(member.id);
              const isCurrentUser = member.id === user.id;
              
              return (
                <div
                  key={member.id}
                  className="flex items-center space-x-3 p-3 hover:bg-emerald-50 rounded-lg transition-colors"
                >
                  <div className="relative">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-400 rounded-full flex items-center justify-center text-white font-semibold">
                      {member.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div
                      className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                        status === 'hidden' 
                          ? 'bg-transparent border-gray-400' 
                          : status === 'online' 
                            ? 'bg-green-500' 
                            : 'bg-gray-400'
                      }`}
                      title={
                        status === 'hidden' 
                          ? 'Hidden' 
                          : status === 'online' 
                            ? 'Online' 
                            : 'Away'
                      }
                    ></div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-emerald-800">
                      {member.name}
                      {isCurrentUser && ' (You)'}
                    </p>
                    <p className="text-xs text-emerald-600">
                      {status === 'hidden' 
                        ? 'Hidden' 
                        : status === 'online' 
                          ? 'Online' 
                          : 'Away'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        </>
      )}

      {/* Delete Message Confirmation Modal */}
      {deletingMessageId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-emerald-800 mb-3">Delete Message</h3>
            <p className="text-emerald-600 mb-6">Are you sure you want to delete this message? This action cannot be undone.</p>
            <div className="flex space-x-3">
              <button
                onClick={() => setDeletingMessageId(null)}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteMessage}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full px-4 py-2 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      {editingGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-emerald-800 mb-4">Edit Group</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-emerald-700 mb-2">
                  Group Name
                </label>
                <input
                  type="text"
                  value={editedGroupName}
                  onChange={(e) => setEditedGroupName(e.target.value)}
                  className="form-input w-full"
                  placeholder="Enter group name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-emerald-700 mb-2">
                  Description
                </label>
                <textarea
                  value={editedGroupDescription}
                  onChange={(e) => setEditedGroupDescription(e.target.value)}
                  className="form-input w-full min-h-[100px] resize-none"
                  placeholder="Describe the purpose of this group"
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setEditingGroup(false)}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGroupEdit}
                className="flex-1 btn-primary"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Group Confirmation Modal */}
      {deletingGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-emerald-800 mb-3">Delete Group</h3>
            <p className="text-emerald-600 mb-6">
              Are you sure you want to delete this group? All messages and data will be permanently deleted. This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setDeletingGroup(false)}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteGroup}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full px-4 py-2 transition-colors"
              >
                Delete Group
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupChat;
